/**
 * Shared utilities for {@link PairRow}, {@link PairCard}, {@link PairsDataTable}, {@link renderPairDetails}
 * (row/list helpers, no JSX).
 */

import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import {
  DISSOCIATION_REASONS,
  PAIR_STATUS_CONFIG,
  PROGRAMME_BADGE_CONFIG,
} from '@/components/pairs/pair-configs';
import type {
  PairActorRef,
  PairFilters,
  PairHistoryEvent,
  PairHistoryEventType,
  PairManagedByRef,
  PairQueryParams,
  PairRecord,
  PairRoleRef,
  ParentInfo,
} from '@/types/remote/pair.types';
import { getUserSituationConfig } from '@/utils/common-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { toLocalDateString } from '@/utils/format-date-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import { getRoleLabel } from '@/utils/role-label-utils';

export { getAbsenceRateColor, getUserSituationConfig } from '@/utils/common-utils';

/** RBAC actions available at the {@link DataTable} level for pairs. */
export interface PairDataTablePageActions {
  canEditRow?: boolean;
  canExportExcel?: boolean;
  canDissociate?: boolean;
  canViewSessions?: boolean;
  canAddOneOffSession?: boolean;
}

/** True when the pair stored status allows adding a one-off session. */
export function canAddOneOffSession(pair: Pick<PairRecord, 'status'>): boolean {
  return pair.status === 'Active';
}

/** Tooltip for the one-off session action (enabled vs grayed). */
export function getAddOneOffSessionTooltip(pair: Pick<PairRecord, 'status'>): string {
  if (canAddOneOffSession(pair)) return 'Ajouter une séance ponctuelle';
  return 'Disponible uniquement pour un binôme au statut Actif';
}

/** Tooltip for the edit action on an active pair. */
export function getEditTooltip(): string {
  return 'Modifier le binôme';
}

/** Tooltip when editing is blocked because the pair is archived. */
export function getArchivedPairEditTooltip(): string {
  return getErrorMessage('cannotEditArchivedPair');
}

/**
 * Normalises a {@link ManagedByValue} filter to the string[] format expected by the API.
 * Returns undefined when no meaningful value is set.
 */
export function buildManagedByParam(
  managedByIds: ManagedByValue | undefined,
): string[] | undefined {
  if (managedByIds === undefined) return undefined;
  if (managedByIds === 'all') return ['all'];
  if (managedByIds === 'unmanaged') return ['unmanaged'];
  if (Array.isArray(managedByIds) && managedByIds.length > 0) return managedByIds;
  return undefined;
}

/** Swap-line shown in the dissociation modal header. */
export function getPairSwapLabel(pair: { tutorName: string; studentName: string }): string {
  return `${pair.tutorName} ↔ ${pair.studentName}`;
}

/** Tooltip for the dissociate action on an active pair. */
export function getDissociateTooltip(): string {
  return 'Dissocier';
}

/** Tooltip when dissociation is blocked because the pair is archived. */
export function getArchivedPairDissociateTooltip(): string {
  return getErrorMessage('cannotUnlinkArchivedPair');
}

/** KPI defaults plus archived flag for row/card display. */
export function deriveKpis(pair: PairRecord) {
  const kpis = pair.kpis;
  return {
    isArchived: pair.status === 'Archived',
    reportsWritten: kpis?.reportsWritten ?? 0,
    reportsTotal: kpis?.reportsTotal ?? kpis?.sessionsCompleted ?? 0,
    sessionsPlanned: (kpis?.sessionsCompleted ?? 0) + (kpis?.sessionsFuture ?? 0),
    sessionsCompleted: kpis?.sessionsCompleted ?? 0,
  };
}

/** Status, program, situations, and edit tooltip for one pair. */
export function deriveDisplay(pair: PairRecord) {
  const kpis = deriveKpis(pair);
  return {
    ...kpis,
    statusCfg: PAIR_STATUS_CONFIG[pair.status],
    progCfg: pair.program ? PROGRAMME_BADGE_CONFIG[pair.program] : undefined,
    tutorSituationCfg: getUserSituationConfig(pair.tutorSituation),
    studentSituationCfg: getUserSituationConfig(pair.studentSituation),
    editTooltip: kpis.isArchived ? getArchivedPairEditTooltip() : getEditTooltip(),
  };
}

/** `YYYY-MM-DD` for API date filters; undefined when empty. */
export function formatPairFilterDateToString(d?: Date): string | undefined {
  if (!d) return undefined;
  return d.toISOString().split('T')[0];
}

/** Custom dates win; otherwise today/week/month/year presets. */
export function resolveDateRange(
  filters: Pick<PairFilters, 'customDateFrom' | 'customDateTo' | 'dateRange'>,
): { dateFrom?: string; dateTo?: string } {
  if (filters.customDateFrom || filters.customDateTo) {
    return {
      dateFrom: formatPairFilterDateToString(filters.customDateFrom),
      dateTo: formatPairFilterDateToString(filters.customDateTo),
    };
  }
  if (!filters.dateRange || filters.dateRange === 'all') return {};

  if (filters.dateRange === 'today') {
    const bounds = resolveListDateRange({ dateRange: 'today' });
    return {
      dateFrom: bounds.dateFrom ? toLocalDateString(bounds.dateFrom) : undefined,
      dateTo: bounds.dateTo ? toLocalDateString(bounds.dateTo) : undefined,
    };
  }

  const now = new Date();
  let from = new Date(now);
  let to = new Date(now);

  if (filters.dateRange === 'week') {
    const daysFromMonday = (from.getDay() + 6) % 7;
    from.setDate(from.getDate() - daysFromMonday);
    from.setHours(0, 0, 0, 0);
    to = new Date(from);
    to.setDate(to.getDate() + 6);
    to.setHours(23, 59, 59, 999);
  } else if (filters.dateRange === 'month') {
    from.setMonth(now.getMonth() - 1);
    to = now;
  } else if (filters.dateRange === 'year') {
    from.setFullYear(now.getFullYear() - 1);
    to = now;
  }

  return {
    dateFrom: from.toISOString().split('T')[0],
    dateTo: to.toISOString().split('T')[0],
  };
}

/** Any search, status, manager, site, program, year, or date besides the default. */
export function hasActiveFilters(filters: PairFilters): boolean {
  return (
    !!filters.search ||
    filters.statuses.length > 0 ||
    filters.coordinatorUserIds.length > 0 ||
    !!filters.managedByIds ||
    !!filters.establishmentId ||
    filters.programs.length > 0 ||
    !!filters.schoolYearId ||
    !!filters.customDateFrom ||
    !!filters.customDateTo ||
    filters.dateRange !== 'all'
  );
}

/** Pin a tutor / student deep-link onto the list query without touching filter chips. */
export function withPinnedPersonFilter(
  filters: PairQueryParams,
  pin: { tutorUserId?: string; studentUserId?: string },
): PairQueryParams {
  return {
    ...filters,
    ...(pin.tutorUserId ? { tutorUserId: pin.tutorUserId } : {}),
    ...(pin.studentUserId ? { studentUserId: pin.studentUserId } : {}),
  };
}

/** List query: omit empty arrays; managed-by via {@link buildManagedByParam}. */
export function buildApiFilters(
  filters: PairFilters,
  page: number,
  pageSize: number,
): PairQueryParams {
  return {
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    search: filters.search || undefined,
    coordinatorUserIds:
      filters.coordinatorUserIds.length > 0 ? filters.coordinatorUserIds : undefined,
    managedByIds: buildManagedByParam(filters.managedByIds),
    establishmentId: filters.establishmentId || undefined,
    programs: filters.programs.length > 0 ? filters.programs : undefined,
    schoolYearId: filters.schoolYearId || undefined,
    ...resolveDateRange(filters),
    page,
    pageSize,
  };
}

/** Add the value if missing, remove it if present. */
export function toggleListValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/** Deduped program {@link Select} options; label prefers name then code. */
export function mapProgramFilterOptions(
  programs: Array<{ id?: string; name?: string; code?: string }> | undefined,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  return (programs ?? []).flatMap((program) => {
    const id = program.id?.trim();
    if (!id || seen.has(id)) return [];
    seen.add(id);
    return [{ value: id, label: program.name ?? program.code ?? program.id }];
  });
}

/** Short `fr-FR` date; em dash when empty. */
export function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Short `fr-FR` date+time; em dash when empty. */
export function formatDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Parent block is shown when email or phone is present. */
export function hasPairParentInfo(parentInfo?: ParentInfo): boolean {
  return Boolean(parentInfo && (parentInfo.parentEmail || parentInfo.parentPhone));
}

/** `count (rate%)` for the absences KPI. */
export function formatPairAbsenceLabel(count: number, rate: number): string {
  return `${count} (${rate.toFixed(0)}%)`;
}

/** One decimal; em dash when the pair has no score. */
export function formatPairAverageScore(averageScore: number | null): string {
  if (averageScore === null) return '—';
  return averageScore.toFixed(1);
}

/** Star `index` (0-based) is filled when it is ≤ the rounded average. */
export function isPairAverageScoreStarFilled(averageScore: number | null, index: number): boolean {
  return averageScore !== null && index + 1 <= Math.round(averageScore);
}

/** Prefer string/number; objects expose `fullName` / `name` / `label`. */
export function formatHistoryDetailValue(value: unknown): string {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value && typeof value === 'object') {
    const named = value as { fullName?: unknown; name?: unknown; label?: unknown };
    if (typeof named.fullName === 'string') return named.fullName;
    if (typeof named.name === 'string') return named.name;
    if (typeof named.label === 'string') return named.label;
  }
  return '—';
}

/** Timeline dot color per `eventType` (`CREATED`, `STATUS_CHANGED`, …). */
export function getPairHistoryEventDotClass(eventType: PairHistoryEventType): string {
  if (eventType === 'CREATED') return 'bg-blue-500';
  if (eventType === 'STATUS_CHANGED') return 'bg-orange-500';
  if (eventType === 'ARCHIVED') return 'bg-red-500';
  if (eventType === 'MANAGER_CHANGED') return 'bg-green-500';
  return '';
}

/** One-line detail: manager `ancien`→`nouveau`, archive `motif`, or status from→to. */
export function formatPairHistoryEventDetails(event: PairHistoryEvent): string | null {
  const details = event.details;
  if (!details || Object.keys(details).length === 0) return null;
  if (event.eventType === 'MANAGER_CHANGED') {
    return `${formatHistoryDetailValue(details.ancien)} → ${formatHistoryDetailValue(details.nouveau)}`;
  }
  if (event.eventType === 'ARCHIVED' && details.motif != null) {
    return `Motif : ${formatHistoryDetailValue(details.motif)}`;
  }
  if (event.eventType === 'STATUS_CHANGED') {
    return `${formatHistoryDetailValue(details.from)} → ${formatHistoryDetailValue(details.to)}`;
  }
  return null;
}

/** Lookup in {@link DISSOCIATION_REASONS} by API `reason` code. */
export function getDissociationReasonConfig(reason: string | undefined) {
  return DISSOCIATION_REASONS.find((item) => item.code === reason);
}

/** API `OTHER` requires a free-text description. */
export function isOtherDissociationReason(reason: string | undefined): boolean {
  return reason === 'OTHER';
}

/** Custom description, or the default other-reason label. */
export function getDissociationOtherLabel(description?: string | null): string {
  return description || 'Autre motif';
}

/** Role chips for a pair actor snapshot (`userRoles[]`, else legacy `userRole` code). */
export function pairActorRoleChips(
  actor: PairActorRef | PairManagedByRef | null | undefined,
): PairRoleRef[] {
  if (!actor) return [];
  if (actor.userRoles && actor.userRoles.length > 0) {
    return actor.userRoles;
  }
  const legacy = actor.userRole?.trim();
  if (!legacy) return [];
  if (legacy.includes(',')) {
    return [{ code: legacy, label: legacy }];
  }
  return [{ code: legacy, label: getRoleLabel(legacy) }];
}
